import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { ContentTable } from 'pdfmake/interfaces';
import { useTranslation } from 'react-i18next';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';
import { UNRATED } from 'src/pages/controls/lookupData';

import { useGetLatestRiskScoresByRiskId } from '@/hooks/queries';
import { useAggregation } from '@/hooks/useAggregation';
import { useRiskScore } from '@/hooks/useRiskScore';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';

/**
 * Current aggregated rating for the risk PDF export (CUS-327).
 *
 * Under an aggregation scoring model the rating is computed into `risk_score`
 * rather than recorded as a `risk_assessment_result`, so the export's
 * assessment table has no rows to show. This reads the same aggregated score
 * the risk overview panel renders, as a single-row table.
 *
 * Returns `null` from the builder when the org is on the default scoring model
 * (`showScore` false) or when no score has been computed yet — the caller then
 * omits the section rather than printing an empty grid.
 */
const useExportAggregatedRating = (
  riskId: string,
  controlType: Risk_Assessment_Result_Control_Type_Enum,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => ContentTable | null, boolean] => {
  const { t } = useTranslation(['common']);
  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('standard', { configType: 'risk_assessment' });
  const score = useRiskScore(riskId);
  // `useRiskScore` reports the loading state of the assessment-results query,
  // not of the two reads this fallback actually depends on: the `risk_score`
  // feed it takes the score from, and the aggregation settings that decide
  // `showScore` (which defaults to the non-aggregation model while in flight).
  // Both are read here purely for their loading state — the query keys are
  // shared, so this adds no request — because a premature `false` would let
  // the export omit the rating sections entirely instead of waiting.
  const { loading: aggregationLoading } = useAggregation();
  const { loading: latestScoreLoading } = useGetLatestRiskScoresByRiskId({
    queryArgs: { riskId },
  });

  const isControlled =
    controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled;
  const formId = isControlled
    ? 'controlled_risk_assessment_result'
    : 'uncontrolled_risk_assessment_result';

  const createExportTable = (): ContentTable | null => {
    if (!score.showScore) {
      return null;
    }

    const rating = isControlled ? score.residualRating : score.inherentRating;
    const overallScore = isControlled
      ? score.residualScore
      : score.inherentScore;
    const likelihood = isControlled
      ? score.residualLikelihood
      : score.inherentLikelihood;
    const impact = isControlled ? score.residualImpact : score.inherentImpact;
    const configId = isControlled
      ? score.residualConfigId
      : score.inherentConfigId;
    const completionDate = isControlled
      ? score.residualCompletionDate
      : score.inherentCompletionDate;

    if (rating == null && overallScore == null) {
      return null;
    }

    return createTable({
      widths: '*',
      body: [
        tableHeaders([
          getStandardFieldLabel(formId, 'Rating'),
          t('ratings.aggregatedScoreHeader'),
          getStandardFieldLabel(formId, 'Impact'),
          getStandardFieldLabel(formId, 'Likelihood'),
          t('ratings.aggregatedCalculatedAtHeader'),
        ]),
        [
          resolveRiskRating({
            likelihood: likelihood ?? null,
            impact: impact ?? null,
            controlType,
            rating,
            configId,
          })?.label ?? UNRATED.label,
          overallScore?.toFixed(1) ?? '-',
          resolveImpact(impact)?.label ?? UNRATED.label,
          resolveLikelihood(likelihood)?.label ?? UNRATED.label,
          toLocalDate(completionDate) ?? '-',
        ],
      ],
    });
  };

  return [
    createExportTable,
    score.loading || aggregationLoading || latestScoreLoading,
  ];
};

export default useExportAggregatedRating;
